import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import TelephoneInput from 'react-phone-number-input/input'

import ClearableInput, {
  SettingsField,
} from '../../../components/Settings/ClearableInput'
import SettingsCard from '../../../components/Settings/SettingsCard'
import { isValidEin, normalizeEin } from '../../../lib/charitySearch'
import {
  formatPhoneNumberToE164,
  isValidUsPhoneDigits,
  usPhoneDigits,
} from '../../../lib/phone'
import { formatEin } from '../../../lib/strings'
import states from '../../../constants/states'
import AvatarImage from '../../account/avatar-image'
import styles from '../../account/settings/ProfileHeaderCard.module.scss'

function slugHelp({
  t,
  slug,
  initialSlug,
  checkingSlug,
  slugTaken,
  slugReserved,
  hardNormalizeSlug,
}) {
  const normalized = hardNormalizeSlug(slug)
  if (checkingSlug) {
    return {
      help: t('business.edit.slug_checking'),
      helpVariant: undefined,
      status: 'empty',
    }
  }
  if (slugReserved) {
    return {
      help: t('business.edit.slug_reserved'),
      helpVariant: 'error',
      status: 'invalid',
    }
  }
  if (slugTaken) {
    return {
      help: t('business.edit.slug_taken'),
      helpVariant: 'error',
      status: 'invalid',
    }
  }
  if (normalized && normalized !== initialSlug) {
    return {
      help: t('business.edit.slug_available'),
      helpVariant: 'success',
      status: 'valid',
    }
  }
  return {
    help: t('business.edit.slug_hint', {
      url: `/profile/business/${normalized || 'your-slug'}`,
    }),
    helpVariant: undefined,
    status: 'empty',
  }
}

export default function BusinessProfileCard({
  business,
  industries,
  localeKey,
  onFieldChange,
  onChangeSlug,
  onAvatarChange,
  onSave,
  saveDisabled,
  isSaving,
  initialSlug,
  checkingSlug,
  slugTaken,
  slugReserved,
  hardNormalizeSlug,
}) {
  const { t } = useTranslation()

  const einDigits = normalizeEin(business?.ein)
  const einStatus =
    einDigits.length === 0
      ? 'empty'
      : isValidEin(einDigits)
        ? 'valid'
        : 'invalid'

  const phoneDigits = usPhoneDigits(business?.phone)
  const phoneStatus =
    phoneDigits.length === 0
      ? 'empty'
      : isValidUsPhoneDigits(business?.phone)
        ? 'valid'
        : 'invalid'

  const slugMeta = slugHelp({
    t,
    slug: business?.slug,
    initialSlug,
    checkingSlug,
    slugTaken,
    slugReserved,
    hardNormalizeSlug,
  })

  return (
    <SettingsCard
      icon="briefcase"
      title={t('business.edit.business_profile')}
      actions={
        <button
          className="btn btn-primary"
          type="button"
          onClick={onSave}
          disabled={saveDisabled || isSaving}
        >
          {isSaving
            ? t('users.edit.saving', 'Saving...')
            : t('business.edit.save')}
        </button>
      }
    >
      <div className={styles.header}>
        <div className={styles.avatar}>
          <AvatarImage
            imageUrl={
              business?.media_attachment?.thumbnail_url ??
              business?.media_attachment?.direct_upload_url ??
              '/images/default-business.png'
            }
            mediaAttachmentType="business"
            onChange={onAvatarChange}
          />
        </div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-md-6">
          <SettingsField label={t('business.edit.name')} htmlFor="name">
            <ClearableInput
              id="name"
              name="name"
              value={business?.name || ''}
              onChange={value => onFieldChange('name', value)}
              onClear={() => onFieldChange('name', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.slug')}
            htmlFor="slug"
            help={slugMeta.help}
            helpVariant={slugMeta.helpVariant}
            status={slugMeta.status}
          >
            <ClearableInput
              id="slug"
              name="slug"
              value={business?.slug || ''}
              onChange={onChangeSlug}
              onClear={() => onChangeSlug('')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.ein')}
            htmlFor="ein"
            status={einStatus}
            help={
              einStatus === 'invalid'
                ? t(
                    'business.edit.ein_invalid',
                    'EIN must be exactly 9 digits.',
                  )
                : t(
                    'business.edit.ein_hint',
                    'Required to verify your business. Structure: XX-XXXXXXX (e.g., 12-3456789).',
                  )
            }
            helpVariant={einStatus === 'invalid' ? 'error' : undefined}
          >
            <ClearableInput
              id="ein"
              name="ein"
              // 9 digits plus the dash formatEin inserts. maxLength 9 would
              // block the last digit of 12-3456789.
              maxLength={10}
              placeholder="12-3456789"
              value={einDigits.length >= 3 ? formatEin(einDigits) : einDigits}
              onChange={value => onFieldChange('ein', normalizeEin(value))}
              onClear={() => onFieldChange('ein', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.contact_number')}
            htmlFor="phone"
            status={phoneStatus}
            help={
              phoneStatus === 'invalid'
                ? t(
                    'business.edit.phone_invalid',
                    'Enter a 10-digit US phone number.',
                  )
                : null
            }
            helpVariant={phoneStatus === 'invalid' ? 'error' : undefined}
          >
            <TelephoneInput
              className="form-control"
              id="phone"
              country="US"
              initialValueFormat="national"
              international={false}
              onChange={phone => onFieldChange('phone', phone || '')}
              placeholder="(123) 456-7890"
              value={formatPhoneNumberToE164(business?.phone)}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.website')}
            htmlFor="website_url"
          >
            <ClearableInput
              id="website_url"
              name="website_url"
              value={business?.website_url || ''}
              onChange={value =>
                onFieldChange('website_url', value.replace(/(^\w+:|^)\/\//, ''))
              }
              onClear={() => onFieldChange('website_url', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.industry')}
            htmlFor="industry_id"
          >
            <select
              name="industry_id"
              id="industry_id"
              className="form-select"
              value={business?.industry_id ?? ''}
              onChange={e => onFieldChange('industry_id', e.target.value)}
            >
              <option value="">{t('users.edit.no_gender')}</option>
              {industries.map(s => (
                <option value={s.id} key={s.id}>
                  {s[localeKey]}
                </option>
              ))}
            </select>
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.address')}
            htmlFor="address_line_1"
          >
            <ClearableInput
              id="address_line_1"
              name="address_line_1"
              value={business?.address_line_1 || ''}
              onChange={value => onFieldChange('address_line_1', value)}
              onClear={() => onFieldChange('address_line_1', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-6">
          <SettingsField
            label={t('business.edit.suite_number')}
            htmlFor="address_line_2"
          >
            <ClearableInput
              id="address_line_2"
              name="address_line_2"
              value={business?.address_line_2 || ''}
              onChange={value => onFieldChange('address_line_2', value)}
              onClear={() => onFieldChange('address_line_2', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-4">
          <SettingsField label={t('business.edit.city')} htmlFor="city">
            <ClearableInput
              id="city"
              name="city"
              value={business?.city || ''}
              onChange={value => onFieldChange('city', value)}
              onClear={() => onFieldChange('city', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>

        <div className="col-md-4">
          <SettingsField label={t('business.edit.state')} htmlFor="state">
            <select
              name="state"
              id="state"
              className="form-select"
              value={business?.state ?? ''}
              onChange={e => onFieldChange('state', e.target.value)}
            >
              <option value="">{t('users.edit.no_gender')}</option>
              {states.map(s => (
                <option value={s.value} key={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </SettingsField>
        </div>

        <div className="col-md-4">
          <SettingsField label={t('business.edit.zip')} htmlFor="zip">
            <ClearableInput
              id="zip"
              name="zip"
              value={business?.zip || ''}
              onChange={value => onFieldChange('zip', value)}
              onClear={() => onFieldChange('zip', '')}
              clearLabel={t('settings.field.clear', 'Clear')}
            />
          </SettingsField>
        </div>
      </div>
    </SettingsCard>
  )
}

BusinessProfileCard.propTypes = {
  business: PropTypes.object,
  industries: PropTypes.array,
  localeKey: PropTypes.string.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onChangeSlug: PropTypes.func.isRequired,
  onAvatarChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  isSaving: PropTypes.bool,
  initialSlug: PropTypes.string,
  checkingSlug: PropTypes.bool,
  slugTaken: PropTypes.bool,
  slugReserved: PropTypes.bool,
  hardNormalizeSlug: PropTypes.func.isRequired,
}
